/*
  Phone country-code field - QUELL-651

  One shared control for every lead form: a native <select> of dialling codes
  beside the phone box (snippets/phone-country-code.liquid), plus the rules
  that join the two into the single international number every destination
  stores, e.g. "+44 7946469854". The rules are a port of SoundBox Store's
  web/src/components/quote/phone.ts (SBS-626), so all three sites agree.

  Dependency-free. The pure functions also run under Node for
  scripts/check-phone-country-code.mjs; DOM binding only happens in a browser.
*/
(function (root) {
  'use strict';

  if (root.QuellPhone) return;

  /* ISO code -> dialling code. Same countries and order as SBS-626. */
  var DIAL_CODES = [
    { iso: 'GB', dial: '+44' },
    { iso: 'IE', dial: '+353' },
    { iso: 'US', dial: '+1' },
    { iso: 'DK', dial: '+45' },
    { iso: 'SE', dial: '+46' },
    { iso: 'DE', dial: '+49' },
    { iso: 'NO', dial: '+47' },
    { iso: 'FR', dial: '+33' },
    { iso: 'NL', dial: '+31' },
    { iso: 'BE', dial: '+32' },
    { iso: 'AT', dial: '+43' },
    { iso: 'CH', dial: '+41' },
    { iso: 'ES', dial: '+34' },
    { iso: 'FI', dial: '+358' },
    { iso: 'PL', dial: '+48' },
    { iso: 'IT', dial: '+39' },
    { iso: 'CA', dial: '+1' },
    { iso: 'AE', dial: '+971' },
    { iso: 'SA', dial: '+966' }
  ];

  /* Fewer digits than this cannot be a phone number. */
  var MIN_DIGITS = 6;

  /* How many national digits (after the trunk 0 is removed) each country's
     numbering plan allows, [min, max]. Ranges, not exact lengths, where a plan
     mixes lengths (ITU-T E.164 national numbering plans). A number typed with
     its own + code is checked against E.164 as a whole instead. */
  var NATIONAL_DIGITS = {
    GB: [9, 10], IE: [7, 9], US: [10, 10], DK: [8, 8], SE: [7, 9], DE: [6, 11],
    NO: [8, 8], FR: [9, 9], NL: [9, 9], BE: [8, 9], AT: [6, 13], CH: [9, 9],
    ES: [9, 9], FI: [6, 12], PL: [9, 9], IT: [6, 11], CA: [10, 10], AE: [8, 9],
    SA: [9, 9]
  };
  var E164_MIN_DIGITS = 7;
  var E164_MAX_DIGITS = 15;
  /* Digits, spaces, brackets, hyphens, dots and a leading + are the only
     characters a phone number is written with. */
  var ALLOWED_CHARS = /^[\d\s().+-]*$/;

  /* Italian numbers keep their leading 0 after the country code (+39 06 ...),
     so the trunk-prefix strip below must not touch them. */
  var KEEPS_LEADING_ZERO = { IT: true };

  function dialCodeFor(iso) {
    var code = String(iso || '').toUpperCase();
    for (var i = 0; i < DIAL_CODES.length; i++) {
      if (DIAL_CODES[i].iso === code) return DIAL_CODES[i].dial;
    }
    return '';
  }

  /* First supported country among the candidates, else ''. Unlike SBS-626
     there is no GB fallback: a wrong code makes WhatsApp follow-up fail, so an
     unlisted market leaves the required select empty and the visitor chooses. */
  function pickPhoneCountry() {
    for (var i = 0; i < arguments.length; i++) {
      if (arguments[i] && dialCodeFor(arguments[i])) return String(arguments[i]).toUpperCase();
    }
    return '';
  }

  function digitCount(value) {
    return String(value || '').replace(/\D/g, '').length;
  }

  /* Longest supported dialling code the typed international number starts with. */
  function dialCodeInNumber(international) {
    var digits = String(international || '').replace(/\D/g, '');
    var best = '';
    for (var i = 0; i < DIAL_CODES.length; i++) {
      var dial = DIAL_CODES[i].dial;
      if (digits.indexOf(dial.slice(1)) === 0 && dial.length > best.length) best = dial;
    }
    return best;
  }

  function isInternational(raw) {
    return raw.charAt(0) === '+' || raw.indexOf('00') === 0;
  }

  function keepsLeadingZero(iso) {
    return !!KEEPS_LEADING_ZERO[String(iso || '').toUpperCase()];
  }

  /* "+<code> <digits>". A number the visitor already typed in international
     form ("+49 ..." or "0049 ...") is kept as they wrote it; its own code wins
     over the dropdown when it is one we recognise. With no usable code the
     digits are returned alone - no code is ever invented. */
  function joinInternationalPhone(iso, typed) {
    var selected = dialCodeFor(iso);
    var raw = String(typed || '').trim();

    if (isInternational(raw)) {
      var international = raw.indexOf('00') === 0 ? '+' + raw.slice(2) : raw;
      return { phone: international, phone_country_code: dialCodeInNumber(international) || selected };
    }

    var digits = raw.replace(/\D/g, '');
    if (!keepsLeadingZero(iso)) digits = digits.replace(/^0+/, '');
    if (!selected) return { phone: digits, phone_country_code: '' };
    return { phone: selected + ' ' + digits, phone_country_code: selected };
  }

  /* The [min, max] national digit range for a country; a generous E.164
     fallback for one we have no plan for. */
  function nationalRange(iso) {
    var code = String(iso || '').toUpperCase();
    if (NATIONAL_DIGITS[code]) return NATIONAL_DIGITS[code];
    var dial = dialCodeFor(code);
    return [MIN_DIGITS, E164_MAX_DIGITS - (dial ? dial.length - 1 : 0)];
  }

  /* Is the typed number a plausible length for the chosen country? null when
     fine, else { kind: 'chars' | 'short' | 'long', iso, min, max }. iso is ''
     when the number carried its own code and was checked against E.164. */
  function checkLength(iso, typed) {
    var raw = String(typed || '').trim();
    if (!raw) return null;
    if (!ALLOWED_CHARS.test(raw)) return { kind: 'chars', iso: '', min: 0, max: 0 };

    var code = String(iso || '').toUpperCase();
    var min, max, digits;
    if (isInternational(raw)) {
      code = '';
      min = E164_MIN_DIGITS;
      max = E164_MAX_DIGITS;
      digits = digitCount(raw.indexOf('00') === 0 ? raw.slice(2) : raw);
    } else {
      var range = nationalRange(code);
      min = Math.max(range[0], MIN_DIGITS);
      max = range[1];
      digits = digitCount(joinInternationalPhone(code, raw).phone) - (dialCodeFor(code).length ? dialCodeFor(code).length - 1 : 0);
    }
    if (digits < min) return { kind: 'short', iso: code, min: min, max: max };
    if (digits > max) return { kind: 'long', iso: code, min: min, max: max };
    return null;
  }

  /* Digit groups of a country's example number: "7700 900000" -> [4, 6]. */
  function groupsOf(example) {
    return String(example || '').split(/\s+/).map(function (g) {
      return g.replace(/\D/g, '').length;
    }).filter(Boolean);
  }

  /* Lays the typed digits out like the country's example: "07700900000" with
     "7700 900000" -> "07700 900000". A trunk 0 stays in front (Italy's belongs
     to the number and is grouped with it). Digits past the example continue
     as one last group; the length check deals with those. A number typed with
     its own + code, or one with stray characters, is left as typed. */
  function formatNational(typed, example, iso) {
    var raw = String(typed || '');
    if (!raw || isInternational(raw.trim()) || !ALLOWED_CHARS.test(raw)) return raw;
    var groups = groupsOf(example);
    if (!groups.length) return raw;
    var digits = raw.replace(/\D/g, '');
    var lead = '';
    if (digits.charAt(0) === '0' && !keepsLeadingZero(iso)) {
      lead = '0';
      digits = digits.slice(1);
    }
    var out = [];
    var i = 0;
    for (var g = 0; g < groups.length && i < digits.length; g++) {
      out.push(digits.substr(i, groups[g]));
      i += groups[g];
    }
    if (i < digits.length) out.push(digits.slice(i));
    return lead + out.join(' ');
  }

  /* Form values as submitted: the select's ISO field is form plumbing, so it
     is replaced by the joined phone and its phone_country_code. An empty phone
     is left alone. keys: { phone, country, code } field names. */
  function withInternationalPhone(values, keys) {
    keys = keys || {};
    var phoneKey = keys.phone || 'contact[phone]';
    var countryKey = keys.country || 'contact[phone_country]';
    var codeKey = keys.code || 'contact[phone_country_code]';
    var out = {};
    Object.keys(values || {}).forEach(function (k) {
      if (k !== countryKey) out[k] = values[k];
    });
    if (!String(out[phoneKey] || '').trim()) return out;
    var joined = joinInternationalPhone(values[countryKey], out[phoneKey]);
    out[phoneKey] = joined.phone;
    if (joined.phone_country_code) out[codeKey] = joined.phone_country_code;
    return out;
  }

  /* ---- DOM ---------------------------------------------------------------- */

  function rowFor(form) {
    return form && form.querySelector ? form.querySelector('[data-phone-country-code]') : null;
  }

  function partsOf(row) {
    return {
      select: row.querySelector('select'),
      input: row.querySelector('input[type="tel"]')
    };
  }

  function keysFor(row) {
    var p = partsOf(row);
    return {
      phone: p.input.name,
      country: p.select.name,
      code: row.getAttribute('data-code-name') || p.input.name + '_country_code'
    };
  }

  function message(row, kind) {
    return row.getAttribute('data-msg-' + kind) || '';
  }

  function selectedOption(select) {
    return select.options[select.selectedIndex] || null;
  }

  function exampleFor(select) {
    var option = selectedOption(select);
    return option ? option.getAttribute('data-example') || '' : '';
  }

  /* The visitor-facing sentence for a checkLength() problem, e.g.
     "GB +44 numbers have 9 to 10 digits." The templates come translated from
     the snippet's data-msg-* attributes; [code] and [range] are filled here. */
  function lengthMessage(row, problem, select) {
    if (problem.kind === 'chars') return message(row, 'chars');
    if (problem.iso) {
      var option = select ? selectedOption(select) : null;
      var label = option && option.value === problem.iso ? option.textContent.trim() : problem.iso;
      var range = problem.min === problem.max ? String(problem.max) : problem.min + ' ' + (message(row, 'to') || 'to') + ' ' + problem.max;
      var template = message(row, 'length');
      if (template) return template.replace('[code]', label).replace('[range]', range);
    }
    return message(row, problem.kind === 'long' ? 'long' : 'short');
  }

  /* The form's flat {name: value} fields with the phone joined. Forms without
     the control get their fields back unchanged. */
  function joinFields(form, fields) {
    var row = rowFor(form);
    return row ? withInternationalPhone(fields, keysFor(row)) : fields;
  }

  /* For forms that run their own validation (bookings, resource download).
     null when fine, else { field, message }. opts.required overrides the
     input's required attribute. */
  function check(form, opts) {
    var row = rowFor(form);
    if (!row) return null;
    var p = partsOf(row);
    var typed = String(p.input.value || '').trim();
    var required = opts && typeof opts.required === 'boolean' ? opts.required : p.input.hasAttribute('required');
    if (!typed) return required ? { field: p.input, message: message(row, 'required') } : null;
    if (!p.select.value) return { field: p.select, message: message(row, 'code') };
    var problem = checkLength(p.select.value, typed);
    if (problem) return { field: p.input, message: lengthMessage(row, problem, p.select) };
    return null;
  }

  function hasOption(select, value) {
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value === value) return true;
    }
    return false;
  }

  /* Rewrites the box in the country's grouping and keeps the caret after the
     same digit it was on, so typing in the middle and deleting feel normal. */
  function reformat(input, example, iso) {
    var before = input.value;
    var after = formatNational(before, example, iso);
    if (after === before) return;
    var caret = typeof input.selectionStart === 'number' ? input.selectionStart : before.length;
    var digitsBefore = before.slice(0, caret).replace(/\D/g, '').length;
    input.value = after;
    var pos = 0;
    var seen = 0;
    while (pos < after.length && seen < digitsBefore) {
      if (/\d/.test(after.charAt(pos))) seen++;
      pos++;
    }
    try { input.setSelectionRange(pos, pos); } catch (e) {}
  }

  function bind(row) {
    var p = partsOf(row);
    if (!p.select || !p.input) return;

    /* Use the chosen country's example for the hint and national grouping.
       Do not cap characters: formatted international numbers can carry a
       different code. Validate their digits without silently truncating input. */
    function refreshCountry() {
      var example = exampleFor(p.select);
      if (example) p.input.placeholder = example;
      reformat(p.input, example, p.select.value);
    }

    /* Native validation already enforces required and the empty code; the
       length rules are the ones it cannot express. */
    function refreshValidity() {
      if (typeof p.input.setCustomValidity !== 'function') return;
      var problem = checkLength(p.select.value, p.input.value);
      p.input.setCustomValidity(problem ? lengthMessage(row, problem, p.select) : '');
    }

    p.select.addEventListener('change', function () {
      row.setAttribute('data-code-dirty', '');
      refreshCountry();
      refreshValidity();
    });
    p.input.addEventListener('input', function () {
      reformat(p.input, exampleFor(p.select), p.select.value);
      refreshValidity();
    });
    refreshCountry();
    refreshValidity();

    /* Quote form: the code follows the Country field until the visitor picks
       a code themselves. Country options carry data-iso; "Other" has none. */
    var followName = row.getAttribute('data-follow-country');
    var form = row.closest ? row.closest('form') : null;
    var country = followName && form ? form.querySelector('[name="' + followName + '"]') : null;
    if (!country) return;
    country.addEventListener('change', function () {
      if (row.hasAttribute('data-code-dirty')) return;
      var option = selectedOption(country);
      var iso = option ? option.getAttribute('data-iso') || '' : '';
      if (iso && dialCodeFor(iso) && hasOption(p.select, iso)) {
        p.select.value = iso;
        refreshCountry();
        refreshValidity();
      }
    });
  }

  function init(scope) {
    var rows = (scope || root.document).querySelectorAll('[data-phone-country-code]:not([data-bound])');
    for (var i = 0; i < rows.length; i++) {
      rows[i].setAttribute('data-bound', '');
      bind(rows[i]);
    }
  }

  root.QuellPhone = {
    DIAL_CODES: DIAL_CODES,
    NATIONAL_DIGITS: NATIONAL_DIGITS,
    MIN_DIGITS: MIN_DIGITS,
    dialCodeFor: dialCodeFor,
    pickPhoneCountry: pickPhoneCountry,
    digitCount: digitCount,
    joinInternationalPhone: joinInternationalPhone,
    withInternationalPhone: withInternationalPhone,
    checkLength: checkLength,
    formatNational: formatNational,
    joinFields: joinFields,
    check: check,
    init: init
  };

  if (root.document) {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', function () { init(); });
    } else {
      init();
    }
  }
})(typeof window !== 'undefined' ? window : this);
